import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CompleteDatasetDataVersionRequest,
  CompleteDatasetDataVersionResponse,
  CreateDatasetDataVersionRequest,
  CreateDatasetDataVersionResponse,
  GetDatasetDataVersionsRequest,
  GetDatasetDataVersionsResponse,
} from '@mai/platform-api/src/routes/dataset-data-versions.schema';

import { type QueryOptions, apiClient } from './axios';

// API functions
export const fetchDatasetDataVersions = async (
  params?: GetDatasetDataVersionsRequest,
): Promise<GetDatasetDataVersionsResponse> => {
  const response = await apiClient.get('/dataset-data-versions', { params });
  return response.data;
};

export const fetchDatasetDataVersionsByDatasetId = async (
  datasetId: string,
): Promise<GetDatasetDataVersionsResponse> => {
  return fetchDatasetDataVersions({ datasetIds: [datasetId] });
};

// React Query hooks
export const useDatasetDataVersions = (
  params?: GetDatasetDataVersionsRequest,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['dataset-data-versions', params],
    queryFn: () => fetchDatasetDataVersions(params),
    enabled: options?.enabled,
    retry: options?.retry,
  });
};

export const useDatasetDataVersionsByDatasetId = (
  datasetId: string | null,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['dataset-data-versions', 'by-dataset', datasetId],
    queryFn: () => fetchDatasetDataVersionsByDatasetId(datasetId!),
    enabled: !!datasetId && options?.enabled !== false,
    retry: options?.retry,
  });
};

// Create data version and get upload URL
export const createDataVersion = async (
  data: CreateDatasetDataVersionRequest,
): Promise<CreateDatasetDataVersionResponse> => {
  const response = await apiClient.post('/dataset-data-versions', data);
  return response.data;
};

// Complete data version upload
export const completeDataVersionUpload = async (
  dataVersionId: string,
  data: CompleteDatasetDataVersionRequest,
): Promise<CompleteDatasetDataVersionResponse> => {
  const response = await apiClient.post(
    `/dataset-data-versions/${dataVersionId}/complete`,
    data,
  );
  return response.data;
};

// Upload file to S3 using presigned URL
export const uploadFileToS3 = async (
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<void> => {
  // Using fetch instead of axios for S3 presigned URL uploads
  // as it avoids CORS preflight issues
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }

  // Simulate progress for fetch (doesn't have built-in progress)
  onProgress?.(100);
};

// React Query mutations
export const useCreateDataVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDataVersion,
    onSuccess: (data) => {
      // Invalidate dataset data versions list
      void queryClient.invalidateQueries({
        queryKey: [
          'dataset-data-versions',
          'by-dataset',
          data.dataVersion.datasetId,
        ],
      });
    },
  });
};

export const useCompleteDataVersionUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dataVersionId,
      data,
    }: {
      dataVersionId: string;
      data: CompleteDatasetDataVersionRequest;
    }) => completeDataVersionUpload(dataVersionId, data),
    onSuccess: () => {
      // Invalidate all dataset data versions queries
      void queryClient.invalidateQueries({
        queryKey: ['dataset-data-versions'],
      });
    },
  });
};

// Get download URL for a data version
export const getDataVersionDownloadUrl = async (
  dataVersionId: string,
): Promise<{ downloadUrl: string }> => {
  const response = await apiClient.get(
    `/dataset-data-versions/${dataVersionId}/download`,
  );
  return response.data;
};
